import { useDisclosure, useToast } from "@chakra-ui/react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "react-query";
import {
  employeeCommutingKeys,
  organizationQuestionnaireKeys,
  useAddEmployeeCommutingItems,
  useDeleteEmployeeCommutingItem,
  useDownloadEmployeeCommutingTemplate,
  useGetEmployeeCommutingItems,
  useImportEmployeeCommutingItems,
  useUpdateEmployeeCommutingItem
} from "../../../../../../hooks/query";
import { useUpdateEmployeeCommutingCategory } from "../../../../../../hooks/query/scope-3";
import CategoryForm from "./CategoryForm";
import * as React from "react";
import { useCallback, useState } from "react";
import { showErrorToast, showSuccessToast } from "../../../../../../utils";
import { useDispatch } from "react-redux";
import { hideAppSpinner, showAppSpinner } from "../../../../../../context/Store/slices/appSpinnerSlice";
import { CategoryGrid } from "../CategoryGrid";
import { DeleteDialog } from "../../../../../DataEntryPlanning/components/DeleteDialog";
import { EmployeeCommutingModalWrapper } from "../CategoryModalWrappers";
import { buildDimensionFormDefaults, withDimensionItems } from "./helpers";
import { targetForCategory } from "../../../../aiIngestTarget";

function CategoryEmployeeCommuting({
                                     defaultValues,
                                     tabKey,
                                     handleCategoryChange,
                                     finalize,
                                     finalizeQuestionnaire,
                                     isPreview,
                                     organizationStructure,
                                     dimensionItems
                                   }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  const { id: questionnaireId } = defaultValues;
  const { t, i18n } = useTranslation("translation", {
    keyPrefix: "organizationQuestionnaire.scope3"
  });
  const { t: globalTranslation } = useTranslation("translation");

  const {
    isOpen: isCategoryModalOpen,
    onOpen: onCategoryModalOpen,
    onClose: onCategoryModalClose
  } = useDisclosure();

  const {
    isOpen: isDeleteItemDialogOpen,
    onOpen: onDeleteItemDialogOpen,
    onClose: onDeleteItemDialogClose
  } = useDisclosure();

  const [selectedItem, setSelectedItem] = useState(null);
  const [categoryModalData, setCategoryModalData] = useState(null);

  const colHeaders = [
    t(`inputs.${tabKey}.modeOfTransport`),
    t(`inputs.${tabKey}.transportSubtype`),
    t(`inputs.${tabKey}.commutingDistance`),
    t(`inputs.${tabKey}.daysWorkedInOfficePerYear`),
    t(`inputs.${tabKey}.numberOfEmployees`)
  ];
  const attributesOrder = ["modeOfTransport", "transportSubtype", "commutingDistance", "daysWorkedInOfficePerYear", "numberOfEmployees"];
  const FORM_INPUT_KEY = "employeeCommuting";

  const {
    handleSubmit,
    formState: { isDirty }
  } = useForm();

  const queryConfig = {
    onSuccess: () => {
      showSuccessToast(toast, "Category Employee Commuting saved!");
      if (finalize) {
        finalizeQuestionnaire(questionnaireId);
      } else {
        queryClient.invalidateQueries(organizationQuestionnaireKeys.orgQuestionnaireById());
        if (!isLoadingOnUpdate) {
          handleCategoryChange();
        }
      }
    },
    onError: err => showErrorToast(toast, err.message)
  };

  const { mutate: update, isLoading: isLoadingOnUpdate } = useUpdateEmployeeCommutingCategory(queryConfig);

  const formId = `formStep${tabKey}`;

  const onSubmit = (() => {
    if (!isPreview) {
      if (isDirty) {
        const requestData = {};
        update({ questionnaireId, data: requestData });
      } else {
        finalize ? finalizeQuestionnaire(questionnaireId) : handleCategoryChange();
      }
    } else {
      handleCategoryChange();
    }
  });

  const prepareSectionValues = useCallback(items => {
    const modeOfTransportKey = "organizationQuestionnaire.scope3.inputs.modeOfTransportOptions";
    const transportSubtypeKey = "organizationQuestionnaire.scope3.inputs.transportSubtypeOptions";

    return items.map(item => {
      if (item.modeOfTransport === "walking" || item.modeOfTransport === "cycling") {
        // Temporary fix for subtype field value when modeOfTransport is either 'walking' or 'cycling'
        item.transportSubtype = "";
      }

      return {
        id: item.id ?? null,
        dimensionItems: item.dimensionItems ?? null,
        dimensionOneItem: item.dimensionItems?.dimensionOneItem ?? null,
        dimensionTwoItem: item.dimensionItems?.dimensionTwoItem ?? null,
        dimensionThreeItem: item.dimensionItems?.dimensionThreeItem ?? null,
        dimensionFourItem: item.dimensionItems?.dimensionFourItem ?? null,
        dimensionFiveItem: item.dimensionItems?.dimensionFiveItem ?? null,
        modeOfTransportValue: item.modeOfTransport,
        modeOfTransport: i18n.exists(`${modeOfTransportKey}.${item.modeOfTransport}`) ?
          t(`inputs.modeOfTransportOptions.${item.modeOfTransport}`) : item.modeOfTransport ?? "",
        transportSubtypeValue: item.transportSubtype,
        transportSubtype: i18n.exists(`${transportSubtypeKey}.${item.modeOfTransport}.${item.transportSubtype}`) ?
          t(`inputs.transportSubtypeOptions.${item.modeOfTransport}.${item.transportSubtype}`) : item.transportSubtype ?? "",
        commutingDistance: item.commutingDistance ?? "",
        daysWorkedInOfficePerYear: item.daysWorkedInOfficePerYear ?? "",
        numberOfEmployees: item.numberOfEmployees ?? ""
      };
    });
  }, [i18n, t]);

  const handleAddItem = () => {
    setCategoryModalData({
      fieldArrayName: FORM_INPUT_KEY,
      columnHeaders: [...colHeaders, ""],
      modalHeaderItemsType: t(`inputs.${FORM_INPUT_KEY}.itemsType`),
      organizationStructure,
      dimensionItems
    });
    onCategoryModalOpen();
  };

  const handleEditItem = (item) => {
    setCategoryModalData({
      fieldArrayName: FORM_INPUT_KEY,
      editData: {
        id: item.id,
        dimensionItems: item.dimensionItems ?? null,
        modeOfTransport: item.modeOfTransportValue,
        transportSubtype: item.transportSubtypeValue,
        commutingDistance: item.commutingDistance,
        daysWorkedInOfficePerYear: item.daysWorkedInOfficePerYear,
        numberOfEmployees: item.numberOfEmployees
      },
      columnHeaders: colHeaders,
      modalHeaderItemsType: t(`inputs.${FORM_INPUT_KEY}.itemsType`),
      organizationStructure,
      dimensionItems
    });
    onCategoryModalOpen();
  };

  const handleDeleteItem = (item) => {
    onDeleteItemDialogOpen();
    setSelectedItem({ id: item.id });
  };

  const handleDeleteItemDialogClose = () => {
    setSelectedItem(null);
    onDeleteItemDialogClose();
  };

  const deleteRecord = () => {
    dispatch(showAppSpinner());
    deleteItem({ questionnaireId, recordId: selectedItem.id });
  };

  const handleCategoryModalClose = () => {
    setCategoryModalData(null);
    onCategoryModalClose();
  };

  const { mutate: addItem } = useAddEmployeeCommutingItems({
    onSuccess: () => {
      handleCategoryModalClose();
      showSuccessToast(toast, globalTranslation("organizationQuestionnaire.page.added"));
      queryClient.invalidateQueries(employeeCommutingKeys.items());
    },
    onError: err => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    onSettled: () => dispatch(hideAppSpinner())
  });

  const { mutate: updateItem } = useUpdateEmployeeCommutingItem({
    onSuccess: () => {
      handleCategoryModalClose();
      showSuccessToast(toast, globalTranslation("organizationQuestionnaire.page.updated"));
      queryClient.invalidateQueries(employeeCommutingKeys.items());
    },
    onError: err => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    onSettled: () => dispatch(hideAppSpinner())
  });

  const { mutate: deleteItem } = useDeleteEmployeeCommutingItem({
    onSuccess: () => {
      handleDeleteItemDialogClose();
      showSuccessToast(toast, globalTranslation("organizationQuestionnaire.page.deleted"));
      queryClient.invalidateQueries(employeeCommutingKeys.items());
    },
    onError: err => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    onSettled: () => dispatch(hideAppSpinner())
  });

  const { mutate: downloadTemplate } = useDownloadEmployeeCommutingTemplate({
    onMutate: () => dispatch(showAppSpinner()),
    onError: err => {
      const parsed = err.response?.data
        ? JSON.parse(String.fromCharCode.apply(null, new Int8Array(err.response.data)))
        : null;
      showErrorToast(toast, parsed?.message ?? err.message);
    },
    onSettled: () => dispatch(hideAppSpinner())
  });

  const { mutate: importItems } = useImportEmployeeCommutingItems({
    onSuccess: () => {
      showSuccessToast(toast, globalTranslation("organizationQuestionnaire.page.imported"));
      queryClient.invalidateQueries(employeeCommutingKeys.items());
    },
    onError: err => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    onSettled: () => dispatch(hideAppSpinner())
  });

  const handleImport = (e) => {
    if (!e.target.files?.[0]) return;
    const formData = new FormData();
    formData.append("file", e.target.files[0]);
    dispatch(showAppSpinner());
    importItems({ questionnaireId, data: formData });
  };

  const handleCategoryModalSubmit = (formData) => {
    const apiData = formData[categoryModalData.fieldArrayName].map(el => {
      const row = { ...el };
      if (row.modeOfTransport === "walking" || row.modeOfTransport === "cycling") {
        // Temporary fix for empty subtype field
        row.transportSubtype = row.modeOfTransport;
      }
      return withDimensionItems(row);
    });

    dispatch(showAppSpinner());
    if (categoryModalData.editData) {
      updateItem({
        questionnaireId,
        recordId: apiData[0].id,
        data: apiData[0]
      });
    } else {
      addItem({
        questionnaireId,
        data: apiData
      });
    }
  };

  const getEmptyRow = () => {
    return {
      modeOfTransport: "",
      transportSubtype: "",
      commutingDistance: "",
      daysWorkedInOfficePerYear: "",
      numberOfEmployees: "",
      ...buildDimensionFormDefaults(organizationStructure, dimensionItems)
    };
  };

  return (
    <CategoryForm onSubmit={handleSubmit(onSubmit)} id={formId} marginTop={4}>
      <CategoryGrid
        questionnaireId={questionnaireId}
        columnHeaders={colHeaders}
        attributesOrder={attributesOrder}
        prepareSectionValues={prepareSectionValues}
        handleAddItem={handleAddItem}
        handleEditItem={handleEditItem}
        handleDeleteItem={handleDeleteItem}
        getItemsFunction={useGetEmployeeCommutingItems}
        isPreview={isPreview}
        organizationStructure={organizationStructure}
        onDownloadTemplate={() => downloadTemplate({ questionnaireId })}
        onImport={handleImport}
        importBtnId={`import-${tabKey}-btn`}
        aiTarget={targetForCategory(tabKey)}
        totalsConfig={{ valueKey: "commutingDistance" }}
      />

      <DeleteDialog
        isOpen={isDeleteItemDialogOpen}
        onClose={handleDeleteItemDialogClose}
        onDelete={deleteRecord}
        entityName={"Item"}
      />

      {
        isCategoryModalOpen && categoryModalData !== null && (
          <EmployeeCommutingModalWrapper
            tabKey={tabKey}
            modalData={categoryModalData}
            attributesOrder={attributesOrder}
            isOpen={isCategoryModalOpen}
            onClose={handleCategoryModalClose}
            onSubmit={handleCategoryModalSubmit}
            getEmptyRow={getEmptyRow}
          />
        )
      }
    </CategoryForm>
  );
}

export default CategoryEmployeeCommuting;